import React, {Fragment} from 'react';
import {Drawer} from 'presentation/components/Drawer';
import {CopySection, IconIllustration} from 'presentation/components';
import {format, isValid, parseISO} from 'date-fns';
import {Divider, Link, ListItem, ListItemText, Skeleton, Stack, Typography} from '@mui/material';
import {CopySectionMaskEnum} from 'presentation/constants';
import {textHandle} from 'presentation/utils/textHandle';
import {ErrorLoadingData} from 'presentation/components/ChangeData/ErrorLoadingData';
import {useMediaQueries} from 'presentation/pages/private/hooks';
import {translations} from 'presentation/translations-new';
import {useReferralDetailsDrawer} from './Hooks/useReferralDetailsDrawer';
import {ReferralDetailsDrawerProps} from './types';
import {MyReferralsTaggingEnum} from 'main/analytics/gaTagsEnum';

const {
	procedureDetailsTitle,
	procedureDetailsErrorTitle,
	phonesLabel,
	phoneCopiedMessage,
	passwordLabel,
	validityLabel,
	passwordCopiedMessage,
	authorizedProcedureLabel,
	authorizedProviderLabel,
	locatedInLabel,
	viewOnMapLabel,
} = translations['pt-br'].privateFlow.myReferrals;
const {failToLoadDescription} = translations['pt-br'].commons;

export function ReferralDetailsDrawer({
	open,
	onClose,
	onOpen,
	cdSenha,
}: ReferralDetailsDrawerProps) {
	const {data, isLoading, locationLink, error, refetch} = useReferralDetailsDrawer(cdSenha);
	const {isMobile} = useMediaQueries();
	const parsedValidityDate = data?.dataValidade ? parseISO(data.dataValidade) : undefined;
	const formattedValidityDate =
		parsedValidityDate && isValid(parsedValidityDate)
			? format(parsedValidityDate, 'dd/MM/yyyy')
			: undefined;
	const providerName = data?.prestador?.nome;
	const providerAddress = data?.prestador?.endereco;
	const showProvider = Boolean(providerName || providerAddress);
	const procedures =
		data?.procedimentos?.filter(
			procedure => procedure.codigoProcedimento || procedure.descricaoProcedimento,
		) ?? [];

	return (
		<Drawer.Root
			open={open}
			onClose={onClose}
			onOpen={onOpen}
			width={'600px'}
			customMaxHeight="80%"
			height={isMobile ? '87%' : '100%'}>
			<Drawer.Header
				onClose={onClose}
				title={procedureDetailsTitle}
				closeBtnDataTagId={
					MyReferralsTaggingEnum.CLICK_FECHAR_VER_DETALHES_MEUS_ENCAMINHAMENTOS
				}
			/>
			{isLoading ? (
				<Drawer.Content sx={{marginTop: '32px', gap: '24px'}}>
					<Skeleton variant="rectangular" height="115px" />
					<Divider />
					<Skeleton variant="rectangular" height="115px" />
					<Divider />
					<Skeleton variant="rectangular" height="115px" />
					<Divider />
					<Skeleton variant="rectangular" height="115px" />
				</Drawer.Content>
			) : null}
			{error && !data ? (
				<Stack marginTop="32px" padding="24px">
					<ErrorLoadingData
						alternativeTitle={procedureDetailsErrorTitle}
						alternativeDescription={failToLoadDescription}
						resetErrorBoundary={() => refetch()}
					/>
				</Stack>
			) : null}
			{!isLoading && !!data && (
				<Drawer.Content sx={{marginTop: '32px', gap: '24px'}}>
					{data.senha ? (
						<Fragment>
							<ListItem sx={{p: 0, margin: 0, gap: 2, alignItems: 'start'}}>
								<IconIllustration
									customIconName="password-info-filled"
									variant="primary"
									size="smaller"
								/>
								<ListItemText
									sx={{margin: 0, gap: 2}}
									primary={
										<Stack
											direction="row"
											justifyContent="space-between"
											marginBottom="5.5px"
											alignItems="center">
											<Typography variant="body2" fontWeight={600}>
												{passwordLabel}
											</Typography>
											{formattedValidityDate ? (
												<Typography
													variant="helperText"
													fontWeight={600}
													color="#CC6302">
													{validityLabel} {formattedValidityDate}
												</Typography>
											) : null}
										</Stack>
									}
									secondary={
										<CopySection
											dataTagId={{
												copy: MyReferralsTaggingEnum.CLICK_COPIAR_SENHA_LIBERADO_MEUS_ENCAMINHAMENTOS,
											}}
											value={data.senha}
											mask={CopySectionMaskEnum.WITHOUT_MASK}
											highlightedIcons
											snackbarMessage={passwordCopiedMessage}
											customPadding="8px 16px"
										/>
									}
								/>
							</ListItem>
							<Divider />
						</Fragment>
					) : null}

					{procedures.length > 0 ? (
						<Fragment>
							{procedures.map((procedimento, index) => (
								<Fragment
									key={`${procedimento.codigoProcedimento ?? 'procedure'}-${index}`}>
									<ListItem sx={{p: 0, m: 0, gap: 2, alignItems: 'start'}}>
										<IconIllustration
											customIconName="check-circle-filled"
											variant="primary"
											size="smaller"
										/>

										<ListItemText
											sx={{m: 0, gap: 2}}
											primary={
												<Typography variant="body2" fontWeight={600}>
													{authorizedProcedureLabel}
												</Typography>
											}
											secondary={[
												procedimento.codigoProcedimento,
												procedimento.descricaoProcedimento
													? textHandle(
															procedimento.descricaoProcedimento,
														).capitalize()
													: null,
											]
												.filter(Boolean)
												.join(' - ')}
										/>
									</ListItem>

									<Divider />
								</Fragment>
							))}
						</Fragment>
					) : null}

					{Array.isArray(data?.telefones) &&
					data.telefones.filter(t => t?.numero).length > 0 ? (
						<Fragment>
							<ListItem sx={{p: 0, m: 0, gap: 2, alignItems: 'start'}}>
								<IconIllustration
									customIconName="phone-filled"
									variant="primary"
									size="smaller"
								/>

								<ListItemText
									sx={{m: 0}}
									primary={
										<Typography variant="body2" mb={1} fontWeight={600}>
											{phonesLabel}
										</Typography>
									}
									secondary={
										<Stack gap="8px">
											{data.telefones
												.filter(t => t?.numero)
												.map((telefone, index) => (
													<CopySection
														dataTagId={{
															copy: MyReferralsTaggingEnum.CLICK_COPIAR_TELEFONE_LIBERADO_MEUS_ENCAMINHAMENTOS,
															call: MyReferralsTaggingEnum.CLICK_LIGAR_LIBERADO_MEUS_ENCAMINHAMENTOS,
														}}
														key={index}
														value={telefone.numero}
														callAction
														highlightedIcons
														snackbarMessage={phoneCopiedMessage}
														customPadding="8px 16px"
													/>
												))}
										</Stack>
									}
								/>
							</ListItem>

							<Divider />
						</Fragment>
					) : null}

					{showProvider ? (
						<ListItem sx={{p: 0, margin: 0, gap: 2, alignItems: 'start'}}>
							<IconIllustration
								customIconName="location-on-filled"
								variant="primary"
								size="smaller"
							/>
							<ListItemText
								sx={{margin: 0, gap: 2}}
								primary={
									<Stack
										direction="row"
										justifyContent="space-between"
										alignItems="center">
										<Typography variant="body2" fontWeight={600}>
											{authorizedProviderLabel}
										</Typography>
									</Stack>
								}
								secondary={
									<Stack gap="8px">
										{providerName ? (
											<Typography variant="body2">
												{textHandle(providerName).capitalize()}
											</Typography>
										) : null}
										{providerAddress ? (
											<Typography variant="body2">
												{locatedInLabel} {providerAddress}
											</Typography>
										) : null}
										{providerAddress ? (
											<Link
												data-tag-id={
													MyReferralsTaggingEnum.CLICK_VER_MAPA_LIBERADO_MEUS_ENCAMINHAMENTOS
												}
												href={locationLink}
												target="_blank"
												rel="noopener noreferrer">
												{viewOnMapLabel}
											</Link>
										) : null}
									</Stack>
								}
							/>
						</ListItem>
					) : null}
				</Drawer.Content>
			)}
		</Drawer.Root>
	);
}
